import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Forum from '../models/Forum';
import useLocale from './useLocale';
import useModal from './useModal';

function useForum() {
  const { l } = useLocale();
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    openModal: replyOpenModal,
    showModal: replyShowModal,
    closeModal: replyCloseModal,
  } = useModal();
  const {
    openModal: editForumOpenModal,
    showModal: editForumShowModal,
    closeModal: editForumCloseModal,
  } = useModal();
  const {
    openModal: editReplyOpenModal,
    showModal: editReplyShowModal,
    closeModal: editReplyCloseModal,
  } = useModal();
  const [forum, setForum] = React.useState({
    title: '',
    description: '',
    replies: [],
    user: { name: '' },
    createdAt: '',
  });
  const replyRef = React.useRef(null);
  const editReplyRef = React.useRef(null);
  const [editReplyId, setEditReplyId] = React.useState(null);

  const getForum = () => {
    Forum.show(id)
      .then(res => setForum(res))
      .catch(err => toast.error(l('general_error')));
  };

  React.useEffect(() => {
    getForum();
  }, [id]);

  const handleSave = e => {
    e.preventDefault();

    Forum.update(id, forum)
      .then(res => toast.success(l('forum_updated')))
      .catch(err => toast.error(l('general_error')));

    editForumCloseModal();
  };

  const handleForumChange = e => {
    const { name, value } = e.target;

    setForum(old => ({
      ...old,
      [name]: value,
    }));
  };

  const handleDelete = id => {
    Forum.delete(id)
      .then(res => {
        toast.success(l('forum_deleted'));
        navigate('/forums');
      })
      .catch(err => toast.error(l('general_error')));
  };

  const handleReply = e => {
    e.preventDefault();

    Forum.reply(id, { reply: replyRef.current.value })
      .then(res => {
        toast.success(l('reply_added'));

        replyRef.current.value = '';

        replyCloseModal();
        getForum();
      })
      .catch(err => {
        toast.error(l('general_error'));
        console.log(err);
      });
  };

  const handleDeleteReply = replyId => {
    Forum.deleteReply(id, replyId)
      .then(res => {
        toast.success(l('reply_deleted'));
        getForum();
      })
      .catch(err => {
        toast.error(l('general_error'));
        console.log(err);
      });
  };

  const handleEditReply = e => {
    e.preventDefault();

    Forum.updateReply(id, editReplyId, { reply: editReplyRef.current.value })
      .then(res => {
        toast.success(l('reply_edited'));

        editReplyCloseModal();

        setEditReplyId(null);
        editReplyRef.current.value = '';

        getForum();
      })
      .catch(err => {
        toast.error(l('general_error'));
        console.log(err);
      });
  };

  return {
    forum,
    handleSave,
    handleReply,
    replyOpenModal,
    replyShowModal,
    replyCloseModal,
    replyRef,
    handleDeleteReply,
    handleDelete,
    editForumOpenModal,
    editForumShowModal,
    editForumCloseModal,
    handleForumChange,
    editReplyOpenModal,
    editReplyShowModal,
    editReplyCloseModal,
    editReplyRef,
    setEditReplyId,
    handleEditReply,
  };
}

export default useForum;
